import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { CompositionPersistenceService } from "../../../librettoEditor/services/CompositionPersistenceService";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";

export type DetachedExportSeed = {
    captionsWordsByClipId?: Record<string, any[]>;
    captionsConfigByClipId?: Record<string, any>;
    textConfigByClipId?: Record<string, any>;
    liveSnapshot?: any;
};

/**
 * Build best-effort detached-export seed from the live composition.
 * This keeps detached renders aligned with what the user currently sees.
 */
export const buildDetachedExportSeed = (): DetachedExportSeed => {
    const seed: DetachedExportSeed = {};
    try {
        const captionsWordsMap: Record<string, any[]> = {};
        const captionsConfigMap: Record<string, any> = {};
        const textConfigMap: Record<string, any> = {};
        const clipRecordsById: Record<string, any> = {};
        const orderedLayerIds: string[] = [];
        const orderedClipIdsByLayerId: Record<string, string[]> = {};
        const layerMetaById: Record<string, { name?: string | null; type?: string | null }> = {};

        const liveComp: any = CompositionService.composition as any;
        for (const tr of (liveComp?.tracks ?? []) as any[]) {
            const layerId = String((tr as any)?.id ?? "");
            if (layerId) {
                orderedLayerIds.push(layerId);
                orderedClipIdsByLayerId[layerId] = [];
                layerMetaById[layerId] = {
                    name: (CompositionStateStore.layers as any)?.[layerId]?.name ?? null,
                    type: typeof (tr as any)?.type === "string" ? String((tr as any).type) : "base",
                };
            }

            for (const c of (tr?.clips ?? []) as any[]) {
                if (!c) continue;
                const type = String(c.type ?? "");
                const id = String(c.id ?? "");
                if (!id) continue;

                if (layerId) {
                    orderedClipIdsByLayerId[layerId].push(id);
                    try {
                        const serialized = CompositionPersistenceService.serializeClipForExportSnapshot(c, { layerId });
                        if (serialized) clipRecordsById[id] = serialized;
                    } catch {}
                }

                if (type === "captions") {
                    try {
                        captionsConfigMap[id] = {
                            scriptClipId: c?.scriptClipId ?? null,
                            timingScope: c?.timingScope ?? null,
                            displayMode: c?.displayMode ?? null,
                            textAlign: c?.textAlign ?? null,
                            textBaseline: c?.textBaseline ?? null,
                            textCase: c?.textCase ?? null,
                            maxWidth: c?.maxWidth ?? null,
                            textStyle: c?.textStyle ? JSON.parse(JSON.stringify(c.textStyle)) : null,
                            highlightStyle: c?.highlightStyle ? JSON.parse(JSON.stringify(c.highlightStyle)) : null,
                            background: c?.background ? JSON.parse(JSON.stringify(c.background)) : null,
                            phraseConfig: c?.phraseConfig ? JSON.parse(JSON.stringify(c.phraseConfig)) : null,
                            sceneStyleOverrides: c?.sceneStyleOverrides
                                ? JSON.parse(JSON.stringify(c.sceneStyleOverrides))
                                : null,
                            position: c?.position ? JSON.parse(JSON.stringify(c.position)) : null,
                            width: c?.width ?? null,
                            height: c?.height ?? null,
                            scale: c?.scale ? JSON.parse(JSON.stringify(c.scale)) : null,
                            rotation: c?.rotation ?? null,
                            alpha: c?.alpha ?? null,
                        };
                    } catch {}

                    const words = (c as any).words;
                    if (Array.isArray(words) && words.length > 0) {
                        captionsWordsMap[id] = words.map((w: any) => ({ ...(w ?? {}) }));
                    }
                    continue;
                }

                if (type === "text") {
                    try {
                        textConfigMap[id] = {
                            text: c?.text ?? null,
                            textAlign: c?.textAlign ?? null,
                            textBaseline: c?.textBaseline ?? null,
                            textCase: c?.textCase ?? null,
                            maxWidth: c?.maxWidth ?? null,
                            textStyle: c?.textStyle ? JSON.parse(JSON.stringify(c.textStyle)) : null,
                            sceneStyleOverrides: c?.sceneStyleOverrides
                                ? JSON.parse(JSON.stringify(c.sceneStyleOverrides))
                                : null,
                            position: c?.position ? JSON.parse(JSON.stringify(c.position)) : null,
                            width: c?.width ?? null,
                            height: c?.height ?? null,
                            scale: c?.scale ? JSON.parse(JSON.stringify(c.scale)) : null,
                            rotation: c?.rotation ?? null,
                            alpha: c?.alpha ?? null,
                        };
                    } catch {}
                }
            }
        }

        if (Object.keys(captionsWordsMap).length > 0) seed.captionsWordsByClipId = captionsWordsMap;
        if (Object.keys(captionsConfigMap).length > 0) seed.captionsConfigByClipId = captionsConfigMap;
        if (Object.keys(textConfigMap).length > 0) seed.textConfigByClipId = textConfigMap;

        seed.liveSnapshot = {
            display: {
                canvasWidth: Number(liveComp?.settings?.width ?? liveComp?.width ?? 1920),
                canvasHeight: Number(liveComp?.settings?.height ?? liveComp?.height ?? 1080),
                backgroundColor: String(liveComp?.settings?.background ?? liveComp?.background ?? "#000000"),
            },
            // Persisted ordering is reverse of composition.tracks (last track first).
            orderedLayerIds: [...orderedLayerIds].reverse(),
            orderedClipIdsByLayerId,
            layerMetaById,
            clipRecordsById,
        };
    } catch {}

    return seed;
};

export default buildDetachedExportSeed;
